/**
 * ProviderLoginPage — dedicated login for payment_agent + ticket_provider.
 */
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Truck, Lock, Mail, Loader2 } from 'lucide-react';

const ProviderLoginPage = () => {
  const { providerLogin, isAuthenticated, isProvider } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  if (isAuthenticated && isProvider()) return <Navigate to="/provider" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrMsg('');
    try {
      const u = await providerLogin(email, password);
      toast.success(`Bienvenido ${u.name}`);
      navigate(u.force_password_change ? '/provider/cuenta' : '/provider');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error de autenticación';
      setErrMsg(msg);
      toast.error(msg, { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <Card className="w-full max-w-md p-8 bg-white shadow-2xl">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto bg-blue-600 rounded-xl flex items-center justify-center mb-3">
            <Truck className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Portal Proveedor</h1>
          <p className="text-xs text-slate-500 mt-1">Agentes de Pago · Proveedores de Tickets</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email" className="pl-9" data-testid="provider-login-email" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required placeholder="Contraseña" className="pl-9" data-testid="provider-login-password" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="provider-login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Iniciar sesión'}
          </Button>
        </form>
        {errMsg && (
          <div className="mt-3 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm text-center" data-testid="provider-login-error">
            {errMsg}
          </div>
        )}
        <p className="text-[10px] text-center text-slate-400 mt-4">Si no tienes acceso, contacta al administrador.</p>
      </Card>
    </div>
  );
};

export default ProviderLoginPage;
